/// <amd-module name="Build/Queue-Panel/StageOptionsControllerView" />
import * as React from "react";

import * as Resources from "Build/Queue-Panel/Resources";
import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { IPipelineStageOption } from "Types";

import { IObservableArray, ObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { Checkbox, TriStateCheckbox } from "VSSUI/Checkbox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { ScrollableList, ListItem, IListItemDetails } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { getSafeId } from "VSSUI/Util";

export interface IStageOptionsControllerViewProps {
    pageContext: IVssPageContext
    store: QueueBuildStore
    pipelineId: number;
    onDismiss: () => void;
}

export class StageOptionsControllerView extends React.Component<IStageOptionsControllerViewProps> {
    private _loadingStages: IObservableValue<boolean>;
    private _stageOptions: IObservableArray<IPipelineStageOption>;
    private _runAllStagesValue: ObservableValue<boolean | undefined>;

    constructor(props: IStageOptionsControllerViewProps) {
        super(props);

        this._loadingStages = new ObservableValue(false);
        this._stageOptions = new ObservableArray([]);
        this._runAllStagesValue = new ObservableValue(true);
        this._initializeStageOptions();
    }

    public render(): JSX.Element {
        return (
            <CustomPanel onDismiss={this.props.onDismiss}>
                <PanelHeader
                    backButtonProps={{ onClick: this.props.onDismiss }}
                    titleProps={{ text: Resources.StagesToRun }}
                    description={Resources.StagesToRunSecondaryText}
                    onDismiss={this.props.onDismiss}
                />
                <PanelContent className="queue-panel-child no-padding">
                    <div className="queue-panel-list-container custom-scrollbar">
                        <Observer loading={this._loadingStages}>
                            {(props: { loading: boolean }) =>
                                props.loading ? (
                                    <div>
                                        <Spinner className="queue-build-spinner" />
                                    </div>
                                ) : (
                                    this._displayStageOptions()
                                )
                            }
                        </Observer>
                    </div>
                </PanelContent>
                <Observer stages={this._stageOptions}>
                    {(props: { stages: IPipelineStageOption[] }) =>
                        <PanelFooter
                            buttonProps={[
                                {
                                    text: Resources.CancelButtonText,
                                    onClick: this.props.onDismiss
                                },
                                {
                                    text: Resources.UseSelectedStages,
                                    primary: true,
                                    disabled: props.stages.length <= 1 || this._skippedStages.length === props.stages.length,
                                    onClick: this._submitStageSelection
                                }
                            ]}
                        />
                    }
                </Observer>
            </CustomPanel>
        );
    }

    private _displayStageOptions(): JSX.Element {
        return (
            <Observer errorMessage={this.props.store.errorMessage} stages={this._stageOptions}>
                {(props: { errorMessage: string, stages: IPipelineStageOption[] }) => {
                    const stageCount = props.stages.length;
                    if (props.errorMessage) {
                        return this._getMessageCard(MessageCardSeverity.Error, props.errorMessage);
                    }
                    else if (stageCount > 1) {
                        return (
                            <>
                                <TriStateCheckbox
                                        label={Resources.RunAllStages}
                                        className="run-all-stages-checkbox"
                                        checked={this._runAllStagesValue} 
                                        onChange={this._changeAllItems}
                                        triState={true}/>
                                <div className="v-scroll-auto custom-scrollbar">
                                    <ScrollableList
                                        itemProvider={this._stageOptions}
                                        renderRow={this._renderRow}
                                        width="100%"
                                        singleClickActivation={true}
                                        onActivate={(event, listRow) => this._onStageSelectionChanged(listRow.index, !listRow.data.skip)}
                                    />
                                    {this._skippedStages.length > 0 &&
                                        this._getMessageCard(MessageCardSeverity.Warning, Resources.SkippedStagesWarning)
                                    }
                                </div>
                            </>
                        );
                    }
                    else if (stageCount === 1) {
                        return this._getMessageCard(MessageCardSeverity.Info, Resources.SingleStagePipeline);
                    }
                    else {
                        return this._getMessageCard(MessageCardSeverity.Error, Resources.UnableToLoadStages)
                    }
                }}
            </Observer>
        );
    }

    private _changeAllItems = (e: React.MouseEvent<HTMLElement>, isChecked: boolean) => {
        if (isChecked === undefined) {
            isChecked = false;
        }
        for (let i = 0; i < this._stageOptions.length; i++) {
            this._changeStageAt(i, !isChecked);
        }
        this._updateSelectAllState();
    }

    private _renderRow = (index: number, item: IPipelineStageOption, details: IListItemDetails<IPipelineStageOption>): JSX.Element => {
        const hiddenAriaDivSafeId = getSafeId(this.c_hiddenAriaDivId);
        return (
            <ListItem key={"list-item" + index} index={index} details={details}>
                <div className="queue-panel-list-row flex-grow flex-row">
                    <div className="flex-column margin-right-8 justify-center">
                        <Checkbox
                            checked={!item.skip} />
                    </div>
                    <div className="flex-column justify-center margin-left-4" aria-describedby={hiddenAriaDivSafeId}>
                        <span className="text-ellipsis primary-text" aria-label={item.name}>{item.name}</span>
                        <span className="font-size text-ellipsis secondary-text">
                            {item.dependsOn.length === 0 ? Resources.NoStageDependencies
                                : format(Resources.DependsOn, item.dependsOn.join(", "))}
                        </span>
                        <div id={hiddenAriaDivSafeId} className="hidden">{Resources.ListItemRoleDescription}</div>
                    </div>
                </div>
            </ListItem>
        );
    }

    private _getMessageCard = (severity: MessageCardSeverity, message: string): JSX.Element => {
        return (
            <div className="margin-vertical-8 padding-horizontal-20">
                <MessageCard className="multiline-error" severity={severity}>
                    {message}
                </MessageCard>
            </div>
        );
    }

    private _initializeStageOptions = () => {
        const currentStageOptions = this.props.store.stageOptions;
        if (currentStageOptions.length === 0) {
            this._loadingStages.value = true;
            this._stageOptions.removeAll();
            this.props.store.fetchPipelineStages().then((stages: IPipelineStageOption[]) => {
                this._copyStageOptions(stages);
                this._loadingStages.value = false;
            });
        }
        else {
            this._copyStageOptions(currentStageOptions.value);
        }
    }

    private _copyStageOptions(source: IPipelineStageOption[]) {
        this._stageOptions.value = source.map(stageOption => ({ ...stageOption }));
    }

    private get _skippedStages() {
        return this._stageOptions.value.filter(s => s.skip);
    }

    private _changeStageAt = (index: number, skipped: boolean) => {
        const stage = this._stageOptions.value[index];
        stage.skip = skipped;
        this._stageOptions.change(index, stage);
    }

    private _updateSelectAllState = () => {
        if (this._skippedStages.length == 0) {
            this._runAllStagesValue.value = true;
        }
        else if (this._skippedStages.length == this._stageOptions.length) {
            this._runAllStagesValue.value = false;
        }
        else {
            this._runAllStagesValue.value = undefined;
        }
    }

    private _onStageSelectionChanged = (index: number, skipped: boolean) => {
        this._changeStageAt(index, skipped);
        this._updateSelectAllState();
    }

    private _submitStageSelection = () => {
        this.props.store.updateStageOptions(this._stageOptions.value);
        this.props.onDismiss();
    }

    private c_hiddenAriaDivId = "hiddenAriaDiv";
}
